import { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useApi } from '../api';
import { encodeCanvasToJpegBudget } from './imageCompress';
import './ImageCropModal.css';

export interface ImageCropModalProps {
  imageUrl: string;
  onCropped: (newUrl: string) => void;
  onClose: () => void;
  /** Optional custom uploader for the cropped image (base64, no data: prefix) →
   *  resolved URL. When omitted, uploads via the admin `/console/upload-image`
   *  endpoint (the original behaviour). Non-admin surfaces (e.g. the student
   *  app) inject their own pipeline. */
  uploadCropped?: (base64: string) => Promise<string>;
  /** Optional label for the apply button (default "Apply crop"). */
  applyLabel?: string;
  /**
   * Encoding for the cropped output. Default 'png' — lossless, correct for the
   * question-diagram surfaces (line art rings badly under JPEG).
   *
   * Photographs of handwriting should pass 'jpeg', which runs the shared budget
   * encoder (~50 KB target). A full-resolution PNG of a page photo is hundreds
   * of KB to several MB, and that payload — not the crop itself — is what makes
   * applying a crop feel slow.
   */
  encode?: 'png' | 'jpeg';
  /**
   * Show the cropped result immediately from local pixels and persist in the
   * background. `onCropped` fires at once with a local object URL, and
   * `uploadCropped` still runs — the caller is responsible for tracking that
   * in-flight work (e.g. PhotoCapture's `hasPendingPhotos`) and for swapping in
   * the durable URL when it resolves via `onPersisted`.
   */
  optimistic?: boolean;
  /** Durable URL, once the background persist finishes (optimistic mode only). */
  onPersisted?: (url: string) => void;
  /** Background persist failed (optimistic mode only) — the local preview is
   *  still on screen, so the caller must surface this. */
  onPersistError?: (err: Error) => void;
}

/** Draw the crop region into a canvas at natural resolution. */
function getCroppedCanvas(image: HTMLImageElement, crop: PixelCrop): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0, 0,
    canvas.width,
    canvas.height,
  );
  return canvas;
}

function getCroppedBlob(image: HTMLImageElement, crop: PixelCrop): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0, 0,
    canvas.width,
    canvas.height,
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas toBlob failed'));
    }, 'image/png');
  });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function blobToBase64Full(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Derive full blob URL from original image URL and a relative path.
 * e.g. original: "https://store.blob.core.windows.net/canvas-images/question-images/old.png"
 *      relative: "canvas-images/question-images/new.png"
 *      result:   "https://store.blob.core.windows.net/canvas-images/question-images/new.png"
 */
function toFullBlobUrl(originalUrl: string, relativePath: string): string {
  try {
    const parsed = new URL(originalUrl);
    return `${parsed.origin}/${relativePath}`;
  } catch {
    return relativePath;
  }
}

export function ImageCropModal({
  imageUrl,
  onCropped,
  onClose,
  uploadCropped,
  applyLabel,
  encode = 'png',
  optimistic = false,
  onPersisted,
  onPersistError,
}: ImageCropModalProps) {
  const { fetchApi } = useApi();
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [localSrc, setLocalSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);

  // Fetch image and convert to data URL to avoid CORS/tainted canvas issues
  useEffect(() => {
    setLoading(true);
    fetch(imageUrl)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load image');
        return res.blob();
      })
      .then(blob => blobToBase64Full(blob))
      .then(dataUrl => {
        setLocalSrc(dataUrl);
        setLoading(false);
      })
      .catch(() => {
        setLocalSrc(imageUrl);
        setLoading(false);
      });
  }, [imageUrl]);

  const handleApply = useCallback(async () => {
    if (!completedCrop || !imgRef.current) return;
    if (completedCrop.width < 1 || completedCrop.height < 1) return;

    setUploading(true);
    setError('');
    try {
      // JPEG runs the shared budget encoder (~50 KB) instead of a
      // full-resolution PNG, which for a page photo is orders of magnitude
      // larger and dominates the apply latency.
      const canvas = getCroppedCanvas(imgRef.current, completedCrop);
      const base64 = encode === 'jpeg'
        ? encodeCanvasToJpegBudget(canvas)
        : await blobToBase64(await getCroppedBlob(imgRef.current, completedCrop));

      const persist = async (): Promise<string> => {
        if (uploadCropped) return uploadCropped(base64);
        const { url } = await fetchApi<{ url: string }>('/console/upload-image', {
          method: 'POST',
          body: JSON.stringify({
            imageBase64: base64,
            contentType: encode === 'jpeg' ? 'image/jpeg' : 'image/png',
          }),
        });
        // API returns full blob URL. Fallback: derive from original imageUrl if relative.
        return url.startsWith('http') ? url : toFullBlobUrl(imageUrl, url);
      };

      if (optimistic) {
        // Show the crop from local pixels straight away — nothing on screen
        // needs the durable URL, only submission does. The caller tracks the
        // in-flight persist and swaps the URL in via onPersisted.
        onCropped(`data:image/${encode === 'jpeg' ? 'jpeg' : 'png'};base64,${base64}`);
        void persist()
          .then(url => onPersisted?.(url))
          .catch(err => onPersistError?.(err instanceof Error ? err : new Error(String(err))));
        return;
      }

      onCropped(await persist());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Crop failed');
      setUploading(false);
    }
  }, [completedCrop, fetchApi, onCropped, uploadCropped, encode, optimistic, onPersisted, onPersistError, imageUrl]);

  return (
    <div className="sui-image-crop-overlay" onClick={onClose}>
      <div className="sui-image-crop-modal" onClick={e => e.stopPropagation()}>
        <div className="sui-image-crop-header">
          <h2>Crop Image</h2>
          <button className="sui-image-crop-close" onClick={onClose}>&times;</button>
        </div>
        <div className="sui-image-crop-body">
          {loading ? (
            <span className="sui-image-crop-loading">Loading image...</span>
          ) : localSrc ? (
            <ReactCrop
              crop={crop}
              onChange={setCrop}
              onComplete={setCompletedCrop}
            >
              <img
                ref={imgRef}
                src={localSrc}
                alt="Crop"
                style={{ maxWidth: '100%', maxHeight: '60vh' }}
              />
            </ReactCrop>
          ) : null}
        </div>
        {error && <span className="sui-image-crop-error">{error}</span>}
        <div className="sui-image-crop-footer">
          <button type="button" className="sui-image-crop-btn-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="sui-image-crop-btn-apply"
            onClick={handleApply}
            disabled={uploading || !completedCrop || completedCrop.width < 1}
          >
            {uploading ? 'Uploading...' : (applyLabel ?? 'Apply Crop')}
          </button>
        </div>
      </div>
    </div>
  );
}
